import * as React from "react";
import {
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Grid,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import {toast} from "react-toastify";
import LoadingDialog from "../components/loadingDialog";
import MultiSelect from "../components/multiSelect/multiSelect";
import {
    CF_CATEGORIES,
    DEGREE_LEVELS,
    EDU_LEVELS,
    WHEN_STARTS,
    upsertClickFeedCampaign,
} from "./clickFeedCampaignsService";

type Props = {
    campaign: any;
    setCampaign: (v: any) => void;
    reload: () => Promise<void>;
    publishers: {id: number; name: string}[];
    programs: {id: number; label: string; subject: string}[];
    trafficTypes: string[];
};

export default function ClickFeedCampaignUpsert({campaign, setCampaign, reload, publishers, programs, trafficTypes}: Props) {
    const [loading, setLoading] = React.useState(false);
    const [form, setForm] = React.useState<any>(null);
    const [selectedPublishers, setSelectedPublishers] = React.useState([] as {id: number; value: string}[]);
    const [selectedPrograms, setSelectedPrograms] = React.useState([] as {id: number; value: string}[]);

    React.useEffect(() => {
        if (!campaign) {
            setForm(null);
            setSelectedPublishers([]);
            setSelectedPrograms([]);
            return;
        }
        const publisherIds: number[] = (campaign.publisherIds || []).map(Number);
        const programIds: number[] = (campaign.programIds || []).map(Number);
        setSelectedPublishers(
            publishers
                .filter((p) => publisherIds.includes(p.id))
                .map((p) => ({id: p.id, value: p.name}))
        );
        setSelectedPrograms(
            programs
                .filter((p) => programIds.includes(p.id))
                .map((p) => ({id: p.id, value: p.label}))
        );
        setForm({
            id: campaign.id,
            hp_campaign_id: campaign.hp_campaign_id ?? "",
            hp_campaign_id_pr: campaign.hp_campaign_id_pr ?? "",
            url: campaign.url || "https://",
            dailyCap: campaign.dailyCap ?? 0,
            monthlyCap: campaign.monthlyCap ?? 0,
            defaultRpm: campaign.defaultRpm ?? 500,
            active: campaign.active !== false,
            zips: campaign.zips || "",
            zipsAllowed: campaign.zipsAllowed !== false,
            publishersAllowed: !!campaign.publishersAllowed,
            militaryOnly: !!campaign.militaryOnly,
            blockAlreadyEnrolled: !!campaign.blockAlreadyEnrolled,
            zeroDailyEom: !!campaign.zeroDailyEom,
            hsMin: campaign.hsMin ?? "",
            hsMax: campaign.hsMax ?? "",
            categories: campaign.categories || [],
            whenStarts: campaign.whenStarts || ["0months", "1months", "3months", "6months"],
            trafficTypesBlocked: campaign.trafficTypesBlocked || [],
            eduLevels: campaign.eduLevels || [1, 2, 3, 4, 5, 6, 7, 8, 9],
            degreeLevels: campaign.degreeLevels || [5, 6, 7, 8, 9],
            managed: !!campaign.managed,
        });
    }, [campaign, publishers, programs]);

    const searchPublishersFn = async (search: string, selected: any[]) => {
        const selectedIds = new Set((selected || []).map((s) => Number(s.id)));
        const s = (search || "").toLowerCase();
        return publishers
            .filter((p) => !selectedIds.has(p.id) && (!s || p.name.toLowerCase().includes(s) || String(p.id).includes(s)))
            .map((p) => ({id: p.id, value: p.name}));
    };

    const searchProgramsFn = async (search: string, selected: any[]) => {
        const selectedIds = new Set((selected || []).map((s) => Number(s.id)));
        const s = (search || "").toLowerCase();
        return programs
            .filter((p) => !selectedIds.has(p.id) && (!s || p.label.toLowerCase().includes(s) || p.subject.toLowerCase().includes(s) || String(p.id).includes(s)))
            .map((p) => ({id: p.id, value: p.label}));
    };

    if (!campaign || !form) {
        return null;
    }

    const setField = (key: string, value: any) => setForm((prev: any) => ({...prev, [key]: value}));

    const toggleInArray = (key: string, value: any, checked: boolean) => {
        setForm((prev: any) => {
            const arr = new Set(prev[key] || []);
            if (checked) {
                arr.add(value);
            } else {
                arr.delete(value);
            }
            return {...prev, [key]: Array.from(arr)};
        });
    };

    const handleClose = (_?: any, reason?: string) => {
        if (reason === "backdropClick") {
            return;
        }
        setCampaign(null);
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const input = {
                id: form.id || undefined,
                hp_campaign_id: Number(form.hp_campaign_id),
                hp_campaign_id_pr: form.hp_campaign_id_pr === "" || form.hp_campaign_id_pr == null
                    ? null
                    : Number(form.hp_campaign_id_pr),
                url: form.url,
                dailyCap: Number(form.dailyCap),
                monthlyCap: Number(form.monthlyCap),
                defaultRpm: Number(form.defaultRpm),
                active: !!form.active,
                zips: form.zips || "",
                zipsAllowed: !!form.zipsAllowed,
                publishersAllowed: !!form.publishersAllowed,
                militaryOnly: !!form.militaryOnly,
                blockAlreadyEnrolled: !!form.blockAlreadyEnrolled,
                zeroDailyEom: !!form.zeroDailyEom,
                hsMin: form.hsMin === "" || form.hsMin == null ? null : Number(form.hsMin),
                hsMax: form.hsMax === "" || form.hsMax == null ? null : Number(form.hsMax),
                categories: form.categories,
                whenStarts: form.whenStarts,
                trafficTypesBlocked: form.trafficTypesBlocked,
                programIds: selectedPrograms.map((p) => Number(p.id)),
                publisherIds: selectedPublishers.map((p) => Number(p.id)),
                eduLevels: form.eduLevels.map(Number),
                degreeLevels: form.degreeLevels.map(Number),
            };
            const result = await upsertClickFeedCampaign(input);
            if (!result.success) {
                toast.error((result.errors || []).join("\n") || "Save failed");
                return;
            }
            toast.success(form.id ? "Campaign updated" : "Campaign created");
            setCampaign(null);
            await reload();
        } catch (e: any) {
            toast.error(e?.message || "Save failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Dialog open={true} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle>{form.id ? `Edit CF Campaign #${form.id}` : "Create CF Campaign"}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{mt: 0}}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="CF Campaign Id" type="number"
                                       value={form.hp_campaign_id}
                                       onChange={(e) => setField("hp_campaign_id", e.target.value)}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="PR Campaign Id" type="number"
                                       value={form.hp_campaign_id_pr}
                                       onChange={(e) => setField("hp_campaign_id_pr", e.target.value)}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="URL" value={form.url}
                                       onChange={(e) => setField("url", e.target.value)}/>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Daily Cap $" type="number"
                                       disabled={form.managed}
                                       value={form.dailyCap}
                                       onChange={(e) => setField("dailyCap", e.target.value)}/>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Monthly Cap $" type="number"
                                       value={form.monthlyCap}
                                       onChange={(e) => setField("monthlyCap", e.target.value)}/>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth size="small" label="Default RPM" type="number"
                                       disabled={form.managed}
                                       value={form.defaultRpm}
                                       onChange={(e) => setField("defaultRpm", e.target.value)}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="HS Grad Year Min" type="number"
                                       value={form.hsMin}
                                       onChange={(e) => setField("hsMin", e.target.value)}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="HS Grad Year Max" type="number"
                                       value={form.hsMax}
                                       onChange={(e) => setField("hsMax", e.target.value)}/>
                        </Grid>
                        <Grid item xs={12}>
                            <FormGroup row>
                                <FormControlLabel control={<Switch checked={form.active} onChange={(e) => setField("active", e.target.checked)}/>} label="Active"/>
                                <FormControlLabel control={<Switch checked={form.zeroDailyEom} onChange={(e) => setField("zeroDailyEom", e.target.checked)}/>} label="Zero Daily Cap on 1st"/>
                                <FormControlLabel control={<Switch checked={form.militaryOnly} onChange={(e) => setField("militaryOnly", e.target.checked)}/>} label="Military Only"/>
                                <FormControlLabel control={<Switch checked={form.blockAlreadyEnrolled} onChange={(e) => setField("blockAlreadyEnrolled", e.target.checked)}/>} label="Block Already Enrolled"/>
                            </FormGroup>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">Categories</Typography>
                            <FormGroup row>
                                {CF_CATEGORIES.map((c) => (
                                    <FormControlLabel key={c}
                                        control={<Checkbox size="small" checked={form.categories.includes(c)}
                                                           onChange={(e) => toggleInArray("categories", c, e.target.checked)}/>}
                                        label={c}/>
                                ))}
                            </FormGroup>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel control={<Switch checked={form.zipsAllowed} onChange={(e) => setField("zipsAllowed", e.target.checked)}/>}
                                              label={form.zipsAllowed ? "Zips Allowed" : "Zips Blocked"}/>
                            <TextField fullWidth size="small" multiline minRows={3} label="Zips"
                                       value={form.zips}
                                       onChange={(e) => setField("zips", e.target.value)}/>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel control={<Switch checked={form.publishersAllowed} onChange={(e) => setField("publishersAllowed", e.target.checked)}/>}
                                              label={form.publishersAllowed ? "Publishers Allowed" : "Publishers Blocked"}/>
                            <MultiSelect
                                searchFunction={searchPublishersFn}
                                selectedItems={selectedPublishers}
                                setSelectedItems={setSelectedPublishers}
                                placeholder="Publishers"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{mb: 0.5}}>Programs</Typography>
                            <MultiSelect
                                searchFunction={searchProgramsFn}
                                selectedItems={selectedPrograms}
                                setSelectedItems={setSelectedPrograms}
                                placeholder="Programs"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2">Edu Levels</Typography>
                            {EDU_LEVELS.map((e) => (
                                <FormControlLabel key={e.id}
                                    control={<Checkbox size="small" checked={form.eduLevels.includes(e.id)}
                                                       onChange={(ev) => toggleInArray("eduLevels", e.id, ev.target.checked)}/>}
                                    label={e.label}/>
                            ))}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2">Degree Looking To Get</Typography>
                            {DEGREE_LEVELS.map((e) => (
                                <FormControlLabel key={e.id}
                                    control={<Checkbox size="small" checked={form.degreeLevels.includes(e.id)}
                                                       onChange={(ev) => toggleInArray("degreeLevels", e.id, ev.target.checked)}/>}
                                    label={e.label}/>
                            ))}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2">When Start</Typography>
                            {WHEN_STARTS.map((e) => (
                                <FormControlLabel key={e.id}
                                    control={<Checkbox size="small" checked={form.whenStarts.includes(e.id)}
                                                       onChange={(ev) => toggleInArray("whenStarts", e.id, ev.target.checked)}/>}
                                    label={e.label}/>
                            ))}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2">Block Traffic Types</Typography>
                            {trafficTypes.map((t) => (
                                <FormControlLabel key={t}
                                    control={<Checkbox size="small" checked={form.trafficTypesBlocked.includes(t)}
                                                       onChange={(ev) => toggleInArray("trafficTypesBlocked", t, ev.target.checked)}/>}
                                    label={t}/>
                            ))}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={loading}>Save</Button>
                </DialogActions>
            </Dialog>
            {loading && <LoadingDialog/>}
        </>
    );
}
